import { useEffect, useState } from 'react';
import { api, ErrorApi, LARGO_MINIMO_CONTRASENA, type DatosInvitacion } from '../api';

/**
 * Pantalla de aceptación de una invitación.
 *
 * Tiene dos formas según si el correo ya tiene cuenta, y la diferencia es de
 * seguridad: a una cuenta existente no se le puede fijar contraseña desde un
 * enlace de invitación —sería regalarle a cualquier administrador la forma de
 * apoderarse de la cuenta de alguien de otro cliente—, así que ahí se pide
 * la contraseña actual para confirmar que es su dueño, nunca una nueva.
 *
 * Se pide acá y no mandando a iniciar sesión aparte: una cuenta que se quedó
 * sin ningún tenant (por ejemplo, la quitaron de su único cliente) no puede
 * loguearse por su cuenta —el login lo rechaza de entrada—, así que «cierra
 * sesión, entra, y vuelve» sería un candado sin llave. Si el navegador ya
 * tiene una sesión válida de esa cuenta, el campo es opcional: el servidor
 * la usa antes de mirar la contraseña.
 */
export function AceptarInvitacion({ token, onListo }: { token: string; onListo: () => void }) {
  const [datos, setDatos] = useState<DatosInvitacion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [nombre, setNombre] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [repetida, setRepetida] = useState('');
  const [contrasenaExistente, setContrasenaExistente] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [requiere2fa, setRequiere2fa] = useState(false);

  useEffect(() => {
    let vigente = true;
    api
      .verInvitacion(token)
      .then((d) => vigente && setDatos(d))
      .catch((err) => vigente && setError(err instanceof ErrorApi ? err.message : 'no se pudo leer'))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [token]);

  const corta = contrasena.length > 0 && contrasena.length < LARGO_MINIMO_CONTRASENA;
  const noCoinciden = repetida.length > 0 && contrasena !== repetida;

  async function aceptar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const args = datos?.yaTieneCuenta
        ? ([undefined, contrasenaExistente || undefined] as const)
        : ([nombre.trim() || undefined, contrasena || undefined] as const);
      const resultado = await api.aceptarInvitacion(token, ...args);
      setRequiere2fa(resultado.requiere2fa === true);
      setListo(true);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo aceptar');
    } finally {
      setEnviando(false);
    }
  }

  if (cargando) return <p className="cargando">Cargando…</p>;

  if (listo) {
    return (
      <div className="acceso">
        <h1>Listo</h1>
        <p>
          {requiere2fa
            ? `Ya quedaste sumado a ${datos?.tenant}. Tu cuenta pide verificación en dos pasos: entra normalmente para completarla.`
            : datos?.yaTieneCuenta
              ? `Ya tienes acceso a ${datos.tenant}. Vas a verlo en el selector de empresa.`
              : 'Tu cuenta quedó creada. Entra con la contraseña que elegiste.'}
        </p>
        <button onClick={onListo}>Ir al acceso</button>
      </div>
    );
  }

  if (!datos) {
    return (
      <div className="acceso">
        <h1>Invitación no válida</h1>
        <p>{error ?? 'El enlace no existe o ya venció. Pídele a quien te invitó que te mande uno nuevo.'}</p>
        <button onClick={onListo}>Ir al acceso</button>
      </div>
    );
  }

  return (
    <div className="acceso">
      <h1>Te invitaron a {datos.tenant}</h1>
      <p>
        <strong>{datos.invitadaPor}</strong> te invitó como <strong>{datos.rol}</strong>, a la
        cuenta <code>{datos.email}</code>.
      </p>

      {datos.yaTieneCuenta ? (
        <>
          <p className="aviso-seguridad">
            Ese correo ya tiene cuenta en la plataforma. Escribe su contraseña para confirmar que eres
            tú — nunca la cambiamos ni la fijamos desde una invitación, solo la comprobamos. Si ya
            iniciaste sesión con ella en este navegador, puedes dejarla en blanco.
          </p>
          <form onSubmit={(e) => void aceptar(e)}>
            <label>
              <span>Contraseña</span>
              <input
                type="password"
                value={contrasenaExistente}
                onChange={(e) => setContrasenaExistente(e.target.value)}
                autoComplete="current-password"
                autoFocus
              />
            </label>
            {error && <p className="error">{error}</p>}
            <button type="submit" disabled={enviando}>
              {enviando ? 'Verificando…' : 'Confirmar y sumar este tenant'}
            </button>
          </form>
          <button className="enlace bajo" onClick={onListo}>
            Ir al acceso
          </button>
        </>
      ) : (
        <form onSubmit={(e) => void aceptar(e)}>
          <label>
            <span>Tu nombre</span>
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus required />
          </label>
          <label>
            <span>Contraseña</span>
            <input
              type="password"
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <label>
            <span>Repítela</span>
            <input
              type="password"
              value={repetida}
              onChange={(e) => setRepetida(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          {corta && (
            <p className="aviso-campo">Te faltan {LARGO_MINIMO_CONTRASENA - contrasena.length} caracteres.</p>
          )}
          {noCoinciden && <p className="aviso-campo">Las dos contraseñas no coinciden.</p>}
          {error && <p className="error">{error}</p>}

          <button type="submit" disabled={enviando || corta || noCoinciden || !nombre.trim()}>
            {enviando ? 'Creando…' : 'Crear mi cuenta'}
          </button>
        </form>
      )}
    </div>
  );
}
